import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Check, Heart, Users, 
  Scissors, AlertCircle, Activity, Hospital, Cigarette,
  Volume2
} from 'lucide-react';
import Layout from '../components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common conditions for checkboxes
const COMMON_CONDITIONS = [
  { id: 'diabetes', en: 'Diabetes', hi: 'मधुमेह (शुगर)' },
  { id: 'heart_disease', en: 'Heart Disease', hi: 'हृदय रोग' },
  { id: 'hypertension', en: 'High Blood Pressure', hi: 'उच्च रक्तचाप (BP)' },
  { id: 'cancer', en: 'Cancer', hi: 'कैंसर' },
  { id: 'stroke', en: 'Stroke', hi: 'स्ट्रोक' },
  { id: 'asthma', en: 'Asthma', hi: 'दमा' },
  { id: 'thyroid', en: 'Thyroid', hi: 'थायराइड' },
  { id: 'arthritis', en: 'Arthritis', hi: 'गठिया' },
  { id: 'kidney_disease', en: 'Kidney Disease', hi: 'गुर्दे की बीमारी' },
  { id: 'none', en: 'None / Not Sure', hi: 'कोई नहीं / पता नहीं' },
];

const ALLERGY_TYPES = [
  { id: 'penicillin', en: 'Penicillin', hi: 'पेनिसिलिन' },
  { id: 'sulfa', en: 'Sulfa Drugs', hi: 'सल्फा दवाएं' },
  { id: 'aspirin', en: 'Aspirin/NSAIDs', hi: 'एस्पिरिन' },
  { id: 'latex', en: 'Latex', hi: 'लेटेक्स' },
  { id: 'peanuts', en: 'Peanuts', hi: 'मूंगफली' },
  { id: 'shellfish', en: 'Shellfish', hi: 'समुद्री भोजन' },
  { id: 'dust', en: 'Dust/Pollen', hi: 'धूल/पराग' },
  { id: 'none', en: 'No Known Allergies', hi: 'कोई एलर्जी नहीं' },
];

const MedicalHistoryIntake = () => {
  const navigate = useNavigate();
  const { language, speak } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Family History
    mother_conditions: [],
    mother_other: '',
    father_conditions: [],
    father_other: '',
    hereditary_conditions: '',
    
    // Past Surgeries
    had_surgeries: null,
    surgeries: [{ name: '', year: '', hospital: '' }],
    
    // Chronic Conditions
    chronic_conditions: [],
    chronic_details: '',
    
    // Allergies
    allergies: [],
    allergy_details: '',
    
    // Lifestyle
    smoking: '',
    alcohol: '',
    exercise: '',
    
    // Hospitalizations
    had_hospitalizations: null,
    hospitalizations: [{ reason: '', year: '' }],
  });

  const t = (en, hi) => language === 'hi' ? hi : en;

  const steps = [
    { 
      id: 'family_mother', 
      title: t("Mother's Health History", "माता का स्वास्थ्य इतिहास"),
      icon: Users,
      description: t("Select any conditions your mother has/had", "अपनी माता की बीमारियां चुनें")
    },
    { 
      id: 'family_father', 
      title: t("Father's Health History", "पिता का स्वास्थ्य इतिहास"),
      icon: Users,
      description: t("Select any conditions your father has/had", "अपने पिता की बीमारियां चुनें")
    },
    { 
      id: 'surgeries', 
      title: t("Past Surgeries", "पिछली सर्जरी"),
      icon: Scissors,
      description: t("Have you had any surgeries?", "क्या आपकी कोई सर्जरी हुई है?")
    },
    { 
      id: 'chronic', 
      title: t("Chronic Conditions", "पुरानी बीमारियां"),
      icon: Activity,
      description: t("Any ongoing health conditions?", "कोई चल रही बीमारी?")
    },
    { 
      id: 'allergies', 
      title: t("Allergies", "एलर्जी"),
      icon: AlertCircle,
      description: t("Any known allergies?", "कोई ज्ञात एलर्जी?")
    },
    { 
      id: 'lifestyle', 
      title: t("Lifestyle", "जीवनशैली"),
      icon: Cigarette,
      description: t("Lifestyle habits", "जीवनशैली की आदतें")
    },
    { 
      id: 'hospitalizations', 
      title: t("Hospitalizations", "अस्पताल में भर्ती"),
      icon: Hospital,
      description: t("Any hospital stays?", "कभी अस्पताल में भर्ती हुए?")
    },
    { 
      id: 'summary', 
      title: t("Review & Submit", "समीक्षा और जमा करें"),
      icon: Check,
      description: t("Review your information", "अपनी जानकारी की समीक्षा करें")
    },
  ];

  const speakCurrentStep = () => {
    const step = steps[currentStep];
    speak(`${step.title}. ${step.description}`);
  };

  const toggleCondition = (field, conditionId) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (conditionId === 'none') {
        return { ...prev, [field]: current.includes('none') ? [] : ['none'] };
      }
      const filtered = current.filter(c => c !== 'none');
      if (filtered.includes(conditionId)) {
        return { ...prev, [field]: filtered.filter(c => c !== conditionId) };
      }
      return { ...prev, [field]: [...filtered, conditionId] };
    });
  };

  const addSurgery = () => {
    setFormData(prev => ({
      ...prev,
      surgeries: [...prev.surgeries, { name: '', year: '', hospital: '' }]
    }));
  };

  const updateSurgery = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      surgeries: prev.surgeries.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const addHospitalization = () => {
    setFormData(prev => ({
      ...prev,
      hospitalizations: [...prev.hospitalizations, { reason: '', year: '' }]
    }));
  };

  const updateHospitalization = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      hospitalizations: prev.hospitalizations.map((h, i) => i === index ? { ...h, [field]: value } : h)
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Save intake data
      await axios.post(`${API_URL}/health/medical-history/intake`, {
        family_history: {
          mother: { conditions: formData.mother_conditions, other: formData.mother_other },
          father: { conditions: formData.father_conditions, other: formData.father_other },
          hereditary: formData.hereditary_conditions
        },
        surgeries: formData.had_surgeries ? formData.surgeries.filter(s => s.name) : [],
        chronic_conditions: {
          conditions: formData.chronic_conditions,
          details: formData.chronic_details
        },
        allergies: {
          types: formData.allergies,
          details: formData.allergy_details
        },
        lifestyle: {
          smoking: formData.smoking,
          alcohol: formData.alcohol,
          exercise: formData.exercise
        },
        hospitalizations: formData.had_hospitalizations ? formData.hospitalizations.filter(h => h.reason) : []
      });

      toast.success(t('Medical history saved successfully!', 'चिकित्सा इतिहास सहेजा गया!'));
      navigate('/medical-history');
    } catch (error) {
      console.error('Error saving intake:', error);
      toast.error(t('Failed to save. Please try again.', 'सहेजने में विफल। कृपया पुनः प्रयास करें।'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (steps[currentStep].id) {
      case 'family_mother':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              {t("Select all conditions your mother has or had:", "अपनी माता की सभी बीमारियां चुनें:")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {COMMON_CONDITIONS.map(condition => (
                <label
                  key={condition.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.mother_conditions.includes(condition.id)
                      ? 'border-[#0D2340] bg-[#0D2340]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.mother_conditions.includes(condition.id)}
                    onCheckedChange={() => toggleCondition('mother_conditions', condition.id)}
                  />
                  <span className="text-sm font-medium">
                    {language === 'hi' ? condition.hi : condition.en}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Label>{t("Other conditions (if any)", "अन्य बीमारियां (यदि कोई हो)")}</Label>
              <Input
                value={formData.mother_other}
                onChange={(e) => setFormData({ ...formData, mother_other: e.target.value })}
                placeholder={t("e.g., Parkinson's, Alzheimer's", "जैसे: पार्किंसन, अल्जाइमर")}
              />
            </div>
          </div>
        );

      case 'family_father':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              {t("Select all conditions your father has or had:", "अपने पिता की सभी बीमारियां चुनें:")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {COMMON_CONDITIONS.map(condition => (
                <label
                  key={condition.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.father_conditions.includes(condition.id)
                      ? 'border-[#0D2340] bg-[#0D2340]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.father_conditions.includes(condition.id)}
                    onCheckedChange={() => toggleCondition('father_conditions', condition.id)}
                  />
                  <span className="text-sm font-medium">
                    {language === 'hi' ? condition.hi : condition.en}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Label>{t("Other conditions (if any)", "अन्य बीमारियां (यदि कोई हो)")}</Label>
              <Input
                value={formData.father_other}
                onChange={(e) => setFormData({ ...formData, father_other: e.target.value })}
                placeholder={t("e.g., Parkinson's, Alzheimer's", "जैसे: पार्किंसन, अल्जाइमर")}
              />
            </div>
          </div>
        );

      case 'surgeries':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              {t("Have you had any surgeries in the past?", "क्या आपकी पहले कोई सर्जरी हुई है?")}
            </p>
            <RadioGroup
              value={formData.had_surgeries === null ? '' : formData.had_surgeries ? 'yes' : 'no'}
              onValueChange={(v) => setFormData({ ...formData, had_surgeries: v === 'yes' })}
              className="flex gap-4"
            >
              <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer ${
                formData.had_surgeries === true ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
              }`}>
                <RadioGroupItem value="yes" />
                <span className="font-medium">{t("Yes", "हां")}</span>
              </label>
              <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer ${
                formData.had_surgeries === false ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
              }`}>
                <RadioGroupItem value="no" />
                <span className="font-medium">{t("No", "नहीं")}</span>
              </label>
            </RadioGroup>

            {formData.had_surgeries && (
              <div className="space-y-4 mt-4 animate-fade-in">
                {formData.surgeries.map((surgery, index) => (
                  <Card key={index} className="bg-slate-50">
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <Label>{t("Surgery Name", "सर्जरी का नाम")}</Label>
                        <Input
                          value={surgery.name}
                          onChange={(e) => updateSurgery(index, 'name', e.target.value)}
                          placeholder={t("e.g., Appendix removal", "जैसे: अपेंडिक्स ऑपरेशन")}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>{t("Year", "वर्ष")}</Label>
                          <Input
                            type="number"
                            value={surgery.year}
                            onChange={(e) => updateSurgery(index, 'year', e.target.value)}
                            placeholder="2020"
                          />
                        </div>
                        <div>
                          <Label>{t("Hospital", "अस्पताल")}</Label>
                          <Input
                            value={surgery.hospital}
                            onChange={(e) => updateSurgery(index, 'hospital', e.target.value)}
                            placeholder={t("Hospital name", "अस्पताल का नाम")}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button variant="outline" onClick={addSurgery} className="w-full">
                  + {t("Add Another Surgery", "और सर्जरी जोड़ें")}
                </Button>
              </div>
            )}
          </div>
        );

      case 'chronic':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              {t("Do you have any ongoing/chronic conditions?", "क्या आपको कोई पुरानी बीमारी है?")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {COMMON_CONDITIONS.filter(c => c.id !== 'none').map(condition => (
                <label
                  key={condition.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.chronic_conditions.includes(condition.id)
                      ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.chronic_conditions.includes(condition.id)}
                    onCheckedChange={() => toggleCondition('chronic_conditions', condition.id)}
                  />
                  <span className="text-sm font-medium">
                    {language === 'hi' ? condition.hi : condition.en}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Label>{t("Details (when diagnosed, current treatment)", "विवरण (कब पता चला, वर्तमान इलाज)")}</Label>
              <Textarea
                value={formData.chronic_details}
                onChange={(e) => setFormData({ ...formData, chronic_details: e.target.value })}
                placeholder={t("e.g., Diabetes since 2018, taking Metformin", "जैसे: 2018 से शुगर, मेटफॉर्मिन ले रहे हैं")}
                rows={3}
              />
            </div>
          </div>
        );

      case 'allergies':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              {t("Do you have any known allergies?", "क्या आपको कोई एलर्जी है?")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {ALLERGY_TYPES.map(allergy => (
                <label
                  key={allergy.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.allergies.includes(allergy.id)
                      ? 'border-[#FF5722] bg-[#FF5722]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.allergies.includes(allergy.id)}
                    onCheckedChange={() => toggleCondition('allergies', allergy.id)}
                  />
                  <span className="text-sm font-medium">
                    {language === 'hi' ? allergy.hi : allergy.en}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Label>{t("Other allergies or reactions", "अन्य एलर्जी या प्रतिक्रिया")}</Label>
              <Textarea
                value={formData.allergy_details}
                onChange={(e) => setFormData({ ...formData, allergy_details: e.target.value })}
                placeholder={t("Describe any other allergies or reactions you've experienced", "कोई अन्य एलर्जी या प्रतिक्रिया का वर्णन करें")}
                rows={2}
              />
            </div>
          </div>
        );

      case 'lifestyle':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-semibold mb-3 block">
                {t("Smoking", "धूम्रपान")}
              </Label>
              <RadioGroup
                value={formData.smoking}
                onValueChange={(v) => setFormData({ ...formData, smoking: v })}
                className="flex gap-2"
              >
                {[
                  { value: 'never', en: 'Never', hi: 'कभी नहीं' },
                  { value: 'former', en: 'Quit', hi: 'छोड़ दिया' },
                  { value: 'current', en: 'Yes', hi: 'हां' },
                ].map(option => (
                  <label key={option.value} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer text-center ${
                    formData.smoking === option.value ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value={option.value} />
                    <span className="text-sm font-medium">{language === 'hi' ? option.hi : option.en}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-base font-semibold mb-3 block">
                {t("Alcohol", "शराब")}
              </Label>
              <RadioGroup
                value={formData.alcohol}
                onValueChange={(v) => setFormData({ ...formData, alcohol: v })}
                className="flex gap-2"
              >
                {[
                  { value: 'never', en: 'Never', hi: 'कभी नहीं' },
                  { value: 'occasional', en: 'Sometimes', hi: 'कभी-कभी' },
                  { value: 'regular', en: 'Regular', hi: 'नियमित' },
                ].map(option => (
                  <label key={option.value} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer text-center ${
                    formData.alcohol === option.value ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value={option.value} />
                    <span className="text-sm font-medium">{language === 'hi' ? option.hi : option.en}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-base font-semibold mb-3 block">
                {t("Exercise", "व्यायाम")}
              </Label>
              <RadioGroup
                value={formData.exercise}
                onValueChange={(v) => setFormData({ ...formData, exercise: v })}
                className="flex gap-2"
              >
                {[
                  { value: 'none', en: 'None', hi: 'नहीं' },
                  { value: 'light', en: 'Light', hi: 'हल्का' },
                  { value: 'regular', en: 'Regular', hi: 'नियमित' },
                ].map(option => (
                  <label key={option.value} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer text-center ${
                    formData.exercise === option.value ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value={option.value} />
                    <span className="text-sm font-medium">{language === 'hi' ? option.hi : option.en}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        );

      case 'hospitalizations':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              {t("Have you ever been hospitalized (other than surgeries)?", "क्या आप कभी अस्पताल में भर्ती हुए (सर्जरी के अलावा)?")}
            </p>
            <RadioGroup
              value={formData.had_hospitalizations === null ? '' : formData.had_hospitalizations ? 'yes' : 'no'}
              onValueChange={(v) => setFormData({ ...formData, had_hospitalizations: v === 'yes' })}
              className="flex gap-4"
            >
              <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer ${
                formData.had_hospitalizations === true ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
              }`}>
                <RadioGroupItem value="yes" />
                <span className="font-medium">{t("Yes", "हां")}</span>
              </label>
              <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer ${
                formData.had_hospitalizations === false ? 'border-[#0D2340] bg-[#0D2340]/5' : 'border-slate-200'
              }`}>
                <RadioGroupItem value="no" />
                <span className="font-medium">{t("No", "नहीं")}</span>
              </label>
            </RadioGroup>

            {formData.had_hospitalizations && (
              <div className="space-y-4 mt-4 animate-fade-in">
                {formData.hospitalizations.map((hosp, index) => (
                  <Card key={index} className="bg-slate-50">
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <Label>{t("Reason", "कारण")}</Label>
                        <Input
                          value={hosp.reason}
                          onChange={(e) => updateHospitalization(index, 'reason', e.target.value)}
                          placeholder={t("e.g., Dengue fever", "जैसे: डेंगू बुखार")}
                        />
                      </div>
                      <div>
                        <Label>{t("Year", "वर्ष")}</Label>
                        <Input
                          type="number"
                          value={hosp.year}
                          onChange={(e) => updateHospitalization(index, 'year', e.target.value)}
                          placeholder="2020"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button variant="outline" onClick={addHospitalization} className="w-full">
                  + {t("Add Another", "और जोड़ें")}
                </Button>
              </div>
            )}
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg text-center mb-6">
              <Check className="h-12 w-12 mx-auto text-green-600 mb-2" />
              <p className="font-semibold text-green-800">
                {t("Almost done! Review your information below.", "लगभग हो गया! नीचे अपनी जानकारी देखें।")}
              </p>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">{t("Family History", "पारिवारिक इतिहास")}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p><strong>{t("Mother:", "माता:")}</strong> {formData.mother_conditions.length > 0 ? formData.mother_conditions.join(', ') : t('None', 'कोई नहीं')}</p>
                <p><strong>{t("Father:", "पिता:")}</strong> {formData.father_conditions.length > 0 ? formData.father_conditions.join(', ') : t('None', 'कोई नहीं')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">{t("Surgeries", "सर्जरी")}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {formData.had_surgeries ? formData.surgeries.filter(s => s.name).map((s, i) => (
                  <p key={i}>{s.name} ({s.year})</p>
                )) : t('None', 'कोई नहीं')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">{t("Chronic Conditions", "पुरानी बीमारियां")}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {formData.chronic_conditions.length > 0 ? formData.chronic_conditions.join(', ') : t('None', 'कोई नहीं')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">{t("Allergies", "एलर्जी")}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {formData.allergies.length > 0 ? formData.allergies.join(', ') : t('None', 'कोई नहीं')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">{t("Lifestyle", "जीवनशैली")}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p><strong>{t("Smoking:", "धूम्रपान:")}</strong> {formData.smoking || '-'}</p>
                <p><strong>{t("Alcohol:", "शराब:")}</strong> {formData.alcohol || '-'}</p>
                <p><strong>{t("Exercise:", "व्यायाम:")}</strong> {formData.exercise || '-'}</p>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-nav">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">
              {t("Step", "चरण")} {currentStep + 1} / {steps.length}
            </span>
            <Button variant="ghost" size="sm" onClick={speakCurrentStep}>
              <Volume2 className="h-4 w-4 mr-1" />
              {t("Read aloud", "सुनें")}
            </Button>
          </div>
          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full transition-all ${
                  index <= currentStep ? 'bg-[#0D2340]' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step Header */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              {React.createElement(steps[currentStep].icon, { 
                className: "h-8 w-8 text-[#0D2340]" 
              })}
              <div>
                <CardTitle className="text-xl font-['Manrope']">
                  {steps[currentStep].title}
                </CardTitle>
                <p className="text-slate-500 text-sm">
                  {steps[currentStep].description}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => currentStep === 0 ? navigate('/medical-history') : setCurrentStep(currentStep - 1)}
            className="flex-1 h-14"
            data-testid="intake-back-btn"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            {currentStep === 0 ? t("Cancel", "रद्द करें") : t("Back", "वापस")}
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              className="flex-1 h-14 bg-[#0D2340]"
              data-testid="intake-next-btn"
            >
              {t("Next", "अगला")}
              <ChevronRight className="h-5 w-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 h-14 bg-[#2E7D32]"
              data-testid="intake-submit-btn"
            >
              {isSubmitting ? '...' : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  {t("Save", "सहेजें")}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MedicalHistoryIntake;
